import axios from 'axios';
import { auth } from '../config/firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.calloctech.com/api';

// ── Shared auth interceptor ───────────────────────────────────────────────────
const addAuth = async (config: any) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

// ── Generic API client (for notifications, students, subscriptions) ───────────
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});
apiClient.interceptors.request.use(addAuth);

// ── Govt API client — always sends X-App-ID: meritxgov ───────────────────────
export const govtApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-App-ID': 'meritxgov',
  },
});
govtApi.interceptors.request.use(addAuth);

// ── Govt admin helpers ────────────────────────────────────────────────────────
export const govtAdminAPI = {
  getStats:      ()                          => govtApi.get('/govt/admin/stats'),
  getExams:      ()                          => govtApi.get('/govt/admin/exams'),
  createExam:    (data: any)                 => govtApi.post('/govt/admin/exams', data),
  updateExam:    (id: string, data: any)     => govtApi.put(`/govt/admin/exams/${id}`, data),

  getSubjects:   (examId?: string)           => govtApi.get('/govt/admin/subjects', { params: examId ? { exam_id: examId } : {} }),
  createSubject: (data: any)                 => govtApi.post('/govt/admin/subjects', data),
  updateSubject: (id: string, data: any)     => govtApi.put(`/govt/admin/subjects/${id}`, data),

  getQuestions:  (params?: any)              => govtApi.get('/govt/admin/questions', { params }),
  createQuestion:(data: any)                 => govtApi.post('/govt/admin/questions', data),
  bulkCreate:    (data: any)                 => govtApi.post('/govt/admin/questions/bulk', data),
  updateQuestion:(id: string, data: any)     => govtApi.put(`/govt/admin/questions/${id}`, data),
  deleteQuestion:(id: string)                => govtApi.delete(`/govt/admin/questions/${id}`),

  getTests:      (params?: any)              => govtApi.get('/govt/admin/tests', { params }),
  createTest:    (data: any)                 => govtApi.post('/govt/admin/tests', data),
  updateTest:    (id: string, data: any)     => govtApi.put(`/govt/admin/tests/${id}`, data),
  deleteTest:    (id: string)                => govtApi.delete(`/govt/admin/tests/${id}`),
  getTestQuestions: (testId: string)         => govtApi.get(`/govt/admin/tests/${testId}/questions`),
  addQuestion:   (testId: string, data: any) => govtApi.post(`/govt/admin/tests/${testId}/questions`, data),
  removeQuestion:(testId: string, qId: string) => govtApi.delete(`/govt/admin/tests/${testId}/questions/${qId}`),

  getAttempts:        (params?: any)              => govtApi.get('/govt/admin/attempts', { params }),
  getSeriesPurchases: (params?: any)              => govtApi.get('/govt/admin/series-purchases', { params }),
};

// ── Users (meritxgov app only) ────────────────────────────────────────────────
export const adminAPI = {
  getUsers:           ()                            => apiClient.get('/admin/users?app_id=meritxgov'),
  getStudentActivity: (id: string)                  => apiClient.get(`/admin/users/${id}/activity`),
  getAppConfig:       ()                            => apiClient.get('/subscriptions/admin/config'),
  updateAppConfig:    (key: string, value: any, description?: string) =>
    apiClient.put(`/subscriptions/admin/config/${key}`, { value, description }),
};

export default apiClient;
